<template>
    <div
        class="kpi-card"
        :style="{ padding: normalizeSize(17, true) + ' ' + normalizeSize(20, true) }"
        :class="{ 'with-weather': showWeather }"
    >
        <div class="kpi-content">
            <div class="kpi-label poppinsSemiBold" :style="{ fontSize: normalizeFontSize(15) }">{{ label }}</div>
            <div class="kpi-value-row">
                <span class="!ppv2-text-white poppinsSemiBold" :style="{ fontSize: normalizeFontSize(17) }">{{
                    formattedValue
                }}</span>
                <div v-if="variation !== null" class="kpi-variation" :class="variationClass">
                    <span class="variation-badge">{{ variationText }}</span>
                    <span class="variation-label">{{ $t('dashboard.kpis.vsYesterday') }}</span>
                </div>
            </div>
        </div>
        <div class="kpi-right">
            <template v-if="showWeather && weather">
                <div class="weather-container">
                    <span class="weather-temp">{{ weather.temp !== undefined ? Math.round(weather.temp) : '--' }}°</span>
                    <img v-if="weather.icon" :src="weatherIconUrl" :alt="weather.description" class="weather-icon" />
                </div>
            </template>
            <div v-else class="kpi-icon ppv2-text-white" :style="{ background: iconBg }">
                <slot name="icon">
                    <i v-if="icon && icon.startsWith('mdi-')" :class="['mdi', icon]"></i>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
export default {
    name: 'DashboardKpiCard',
    props: {
        label: {
            type: String,
            required: true,
        },
        value: {
            type: [Number, String],
            default: 0,
        },
        variation: {
            type: Number,
            default: null,
        },
        icon: {
            type: Object,
        },
        iconBg: {
            type: String,
            default: '#7D2DBD',
        },
        iconColor: {
            type: String,
            default: 'white',
        },
        showWeather: {
            type: Boolean,
            default: false,
        },
        weather: {
            type: Object,
            default: null,
        },
        suffix: {
            type: String,
            default: '',
        },
    },
    mounted() {
        if (this.showWeather) {
            console.log(`DashboardKpiCard [${this.label}]: mounted with showWeather=true, weather:`, this.weather)
        }
    },
    watch: {
        weather: {
            handler(newVal) {
                if (this.showWeather) {
                    console.log(`DashboardKpiCard [${this.label}]: weather updated:`, newVal)
                }
            },
            immediate: true,
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
    },
    computed: {
        formattedValue() {
            if (typeof this.value === 'number') {
                return this.value.toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US') + this.suffix
            }
            return this.value + this.suffix
        },
        variationClass() {
            if (this.variation > 0) return 'positive'
            if (this.variation < 0) return 'negative'
            return 'neutral'
        },
        variationText() {
            if (this.variation > 0) return `+${this.variation}%`
            return `${this.variation}%`
        },
        weatherIconUrl() {
            if (this.weather && this.weather.icon) {
                return `https://openweathermap.org/img/wn/${this.weather.icon}@2x.png`
            }
            return ''
        },
    },
}
</script>

<style scoped>
.kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 17px 21px;
    border-radius: 19px;
    background: linear-gradient(127deg, rgba(6, 11, 38, 0.74) 28.26%, rgba(26, 31, 55, 0.5) 91.2%);
    backdrop-filter: blur(57px);
    min-width: 280px;
    flex: 1;
}

.kpi-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.kpi-label {
    color: #a0aec0;
    font-size: clamp(12px, 0.729545vw, 16px);
}

.kpi-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kpi-variation {
    display: flex;
    align-items: center;
    gap: 4px;
}

.variation-badge {
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 400;
}

.kpi-variation.positive .variation-badge {
    background: #00452c;
    color: #00d589;
}

.kpi-variation.negative .variation-badge {
    background: #37242f;
    color: #c52b21;
}

.kpi-variation.neutral .variation-badge {
    background: rgba(255, 255, 255, 0.1);
    color: #a0aec0;
}

.variation-label {
    color: #a0aec0;
    font-size: 12px;
}

.kpi-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kpi-icon {
    width: 43px;
    height: 43px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.02);
}

.kpi-icon :deep(svg),
.kpi-icon svg {
    width: 24px !important;
    height: 24px !important;
}

.weather-container {
    display: flex;
    align-items: center;
    gap: 4px;
}

.weather-temp {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 18px;
    font-weight: 600;
}

.weather-icon {
    width: 32px;
    height: 32px;
}
</style>
